/** Store analytics: traffic, funnel, sources, products. Route /sites/:id/analytics. Orders and revenue are live from the shop's orders. */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, DatePicker, Dropdown, Empty, Row, Segmented, Space, Spin, Switch, Table, Tooltip, Typography, theme, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DownloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { useTranslation } from '../../../shared/i18n';
import { countryName } from '../../../shared/components/CountryFlag';
import { money, sitePath, toastErr, useLoad, useSite } from './common';

const { Text } = Typography;
const FMT = 'YYYY-MM-DD';
const STACK = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 }; // wide tables scroll inside their card
const lastDays = (n) => [dayjs().subtract(n - 1, 'day'), dayjs()];
const pct = (x) => `${(Number(x || 0) * 100).toFixed(1)}%`;
const num = (x) => Number(x || 0).toLocaleString();

const Tile = ({ label, value, cur, prev, compare }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    let delta = null;
    if (compare && prev !== undefined) {
        const c = Number(cur) || 0;
        const p = Number(prev) || 0;
        if (p === 0) delta = <Text type="secondary" style={{ fontSize: 12 }}>{c ? t('New') : '–'}</Text>;
        else {
            const d = ((c - p) / p) * 100;
            const up = d >= 0;
            delta = (
                <Tooltip title={t('Previous period')}>
                    <span style={{ fontSize: 12, color: d === 0 ? token.colorTextTertiary : (up ? token.colorSuccessText : token.colorErrorText) }}>
                        {d !== 0 && (up ? <ArrowUpOutlined /> : <ArrowDownOutlined />)} {Math.abs(d).toFixed(1)}%
                    </span>
                </Tooltip>
            );
        }
    }
    return (
        <div data-kpi={label} style={{ padding: 12, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, height: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
            <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>{value}</div>
            <div style={{ minHeight: 18 }}>{delta}</div>
        </div>
    );
};

const VisitorsChart = ({ series, previous, compare }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const dark = token.colorBgContainer !== '#ffffff' && token.colorTextBase === '#fff';
    const main = dark ? '#3987e5' : '#2a78d6';
    const prev = compare ? (previous?.series || []) : [];
    const curName = t('This period');
    const prevName = t('Previous period');
    const option = {
        animation: false,
        grid: { left: 40, right: 12, top: prev.length ? 32 : 12, bottom: 24 },
        legend: prev.length ? { top: 0, right: 0, icon: 'roundRect', itemWidth: 12, itemHeight: 2, textStyle: { color: token.colorTextSecondary }, data: [curName, prevName] } : { show: false },
        tooltip: {
            trigger: 'axis', axisPointer: { type: 'line', lineStyle: { color: token.colorBorder } }, backgroundColor: token.colorBgElevated, borderColor: token.colorBorderSecondary, textStyle: { color: token.colorText, fontSize: 12 },
            formatter: (ps) => ps.map((p) => {
                const date = p.seriesName === prevName ? prev[p.dataIndex]?.date : series[p.dataIndex]?.date;
                return `<div style="display:flex;gap:12px;justify-content:space-between"><span>${p.marker}${date || ''}</span><b>${num(p.value)}</b></div>`;
            }).join(''),
        },
        xAxis: { type: 'category', boundaryGap: false, data: series.map((x) => String(x.date).slice(5)), axisTick: { show: false }, axisLine: { lineStyle: { color: token.colorBorder } }, axisLabel: { color: token.colorTextTertiary, hideOverlap: true } },
        yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: token.colorBorderSecondary } }, axisLabel: { color: token.colorTextTertiary } },
        series: [
            { name: curName, type: 'line', data: series.map((x) => x.sessions), showSymbol: series.length === 1, symbolSize: 8, lineStyle: { width: 2, color: main }, itemStyle: { color: main }, emphasis: { disabled: true }, z: 2 },
            ...(prev.length ? [{ name: prevName, type: 'line', data: prev.slice(0, series.length).map((x) => x.sessions), showSymbol: false, lineStyle: { width: 2, type: 'dashed', color: token.colorTextQuaternary }, itemStyle: { color: token.colorTextQuaternary }, emphasis: { disabled: true }, z: 1 }] : []),
        ],
    };
    return <ReactECharts style={{ height: 260 }} notMerge option={option} />;
};

const Funnel = ({ steps }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const NAMES = { sessions: t('Visitors'), product_views: t('Viewed a product'), add_to_cart: t('Added to cart'), checkout: t('Started checkout'), paid: t('Paid') };
    const top = Math.max(1, ...steps.map((s) => s.count));
    return (
        <div>
            {steps.map((s, i) => {
                const before = i > 0 ? steps[i - 1].count : null;
                return (
                    <div key={s.step} data-funnel={s.step} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                            <span>{NAMES[s.step] || s.step}</span>
                            <span><b style={{ fontWeight: 600 }}>{num(s.count)}</b>{before !== null && <Text type="secondary" style={{ marginInlineStart: 8, display: 'inline-block', minWidth: 48, textAlign: 'right' }}>{before ? pct(s.count / before) : '–'}</Text>}</span>
                        </div>
                        <div style={{ height: 8, background: token.colorFillQuaternary, borderRadius: 4, marginTop: 3 }}>
                            <div style={{ width: `${(s.count / top) * 100}%`, minWidth: s.count ? 4 : 0, height: '100%', background: token.colorTextBase === '#fff' ? '#3987e5' : '#2a78d6', borderRadius: 4 }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Block = ({ title, kind, exporter, children }) => {
    const { t } = useTranslation();
    return (
        <Card size="small" title={title} style={{ height: '100%' }}
            extra={kind ? <Tooltip title={t('Export CSV')}><Button size="small" type="text" icon={<DownloadOutlined />} aria-label={t('Export CSV')} onClick={() => exporter(kind)} /></Tooltip> : null}>
            {children}
        </Card>
    );
};
const Mini = ({ rows, columns, rowKey }) => {
    const { t } = useTranslation();
    return <Table size="small" rowKey={rowKey} pagination={rows.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: false } : false} dataSource={rows} columns={columns} scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Text type="secondary">{t('No data yet')}</Text> }} />;
};

const Analytics = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId } = useSite();
    const [range, setRange] = useState(() => lastDays(30));
    const [preset, setPreset] = useState(30);
    const [compare, setCompare] = useState(true);
    const from = range[0].format(FMT);
    const to = range[1].format(FMT);
    const { data, loading } = useLoad(() => api.get('/analytics', { from, to, ...(compare ? { compare: 1 } : {}) }), [api, from, to, compare]);
    const cur = data?.currency || 'USD';
    const totals = data?.totals || {};
    const prev = data?.previous?.totals;

    const exporter = async (kind) => {
        try { await api.download('/analytics/export', `analytics-${kind}-${from}-${to}.csv`, { kind, from, to }); message.success(t('Export ready')); } catch (e) { toastErr(e, t('Export failed')); }
    };
    const CHANNEL = useMemo(() => ({ direct: t('Direct', { context: 'traffic' }), organic: t('Organic search'), social: t('Social'), paid: t('Paid ads'), email: t('Email'), referral: t('Referral'), campaign: t('Campaign'), unattributed: t('Unattributed') }), [t]);

    const sales = [
        { title: t('Visitors'), dataIndex: 'sessions', align: 'right', render: num },
        { title: t('Orders'), dataIndex: 'orders', align: 'right', render: num },
        { title: t('Revenue'), dataIndex: 'revenue', align: 'right', render: (v) => money(v, cur) },
    ];
    const productLink = (v, r) => {
        const name = v || r.handle;
        return r.product_id ? <a onClick={() => navigate(sitePath(siteId, `products/${r.product_id}`))}>{name}</a> : name;
    };
    const products = data?.top_products || [];
    const byRevenue = useMemo(() => [...products].filter((x) => x.revenue > 0).sort((a, b) => b.revenue - a.revenue), [products]);
    const byViews = useMemo(() => [...products].filter((x) => x.views > 0).sort((a, b) => b.views - a.views), [products]);
    const empty = data && !totals.sessions && !totals.orders && !totals.pageviews;

    return (
        <div style={STACK}>
            <Card size="small">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <Segmented value={preset} onChange={(n) => { setPreset(n); setRange(lastDays(n)); }}
                        options={[7, 30, 90].map((n) => ({ value: n, label: t('{{n}} days', { n }) }))} />
                    <DatePicker.RangePicker allowClear={false} value={range} disabledDate={(d) => d.isAfter(dayjs(), 'day')} style={{ maxWidth: 260 }}
                        onChange={(v) => { if (v?.[0] && v?.[1]) { setRange(v); setPreset(null); } }} />
                    <Space size={6}><Switch size="small" checked={compare} onChange={setCompare} /><Text style={{ fontSize: 13 }}>{t('Compare')}</Text></Space>
                    <div style={{ flex: 1 }} />
                    <Dropdown menu={{
                        items: [
                            { key: 'daily', label: t('Daily') }, { key: 'channels', label: t('Channels') }, { key: 'utm', label: 'UTM' },
                            { key: 'pages', label: t('Pages') }, { key: 'products', label: t('Products', { context: 'shop' }) },
                        ],
                        onClick: ({ key }) => exporter(key),
                    }}>
                        <Button icon={<DownloadOutlined />}>{t('Export CSV')}</Button>
                    </Dropdown>
                </div>
            </Card>
            <Spin spinning={loading}>
                {!data && <div style={{ height: 200 }} />}
                {empty && (
                    <Card><Empty description={t('No visits in this period')}>
                        <Text type="secondary">{t('Visits appear here within minutes after your published site gets traffic.')}</Text>
                    </Empty></Card>
                )}
                {data && !empty && (
                    <div style={STACK}>
                        <Row gutter={[8, 8]}>
                            {[
                                [t('Visitors'), num(totals.sessions), 'sessions'],
                                [t('Pageviews'), num(totals.pageviews), 'pageviews'],
                                [t('Orders'), num(totals.orders), 'orders'],
                                [t('Revenue'), money(totals.revenue, cur), 'revenue'],
                                [t('Conversion rate'), pct(totals.conversion_rate), 'conversion_rate'],
                                [t('Returning customers'), pct(totals.returning_customer_rate), 'returning_customer_rate'],
                            ].map(([label, value, k]) => (
                                <Col xs={12} md={8} xl={4} key={k}><Tile label={label} value={value} cur={totals[k]} prev={prev?.[k]} compare={compare} /></Col>
                            ))}
                        </Row>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} xl={16}><Block title={t('Visitors')} kind="daily" exporter={exporter}><VisitorsChart series={data.series || []} previous={data.previous} compare={compare} /></Block></Col>
                            <Col xs={24} xl={8}><Block title={t('Funnel')}><Funnel steps={data.funnel || []} /></Block></Col>
                        </Row>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} xl={10}><Block title={t('Sales by channel')} kind="channels" exporter={exporter}>
                                <Mini rows={data.channels || []} rowKey="channel" columns={[{ title: t('Channel'), dataIndex: 'channel', render: (v) => CHANNEL[v] || v }, ...sales]} />
                            </Block></Col>
                            <Col xs={24} xl={14}><Block title={t('Sales by UTM')} kind="utm" exporter={exporter}>
                                <Mini rows={data.utm || []} rowKey={(r) => `${r.source}|${r.medium}|${r.campaign}`} columns={[
                                    { title: t('Source'), dataIndex: 'source', render: (v) => v || '-' }, { title: t('Medium'), dataIndex: 'medium', render: (v) => v || '-' },
                                    { title: t('Campaign'), dataIndex: 'campaign', render: (v) => v || '-' }, ...sales,
                                ]} />
                            </Block></Col>
                        </Row>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} xl={12}><Block title={t('Top products by revenue')} kind="products" exporter={exporter}>
                                <Mini rows={byRevenue} rowKey="handle" columns={[{ title: t('Product', { context: 'shop' }), dataIndex: 'title', render: productLink }, { title: t('Sold'), dataIndex: 'quantity', align: 'right', render: num }, { title: t('Revenue'), dataIndex: 'revenue', align: 'right', render: (v) => money(v, cur) }]} />
                            </Block></Col>
                            <Col xs={24} xl={12}><Block title={t('Top products by views')}>
                                <Mini rows={byViews} rowKey="handle" columns={[{ title: t('Product', { context: 'shop' }), dataIndex: 'title', render: productLink }, { title: t('Views'), dataIndex: 'views', align: 'right', render: num }, { title: t('Added to cart'), dataIndex: 'add_to_cart', align: 'right', render: num }]} />
                            </Block></Col>
                        </Row>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} xl={10}><Block title={t('Top pages')} kind="pages" exporter={exporter}>
                                <Mini rows={data.top_pages || []} rowKey="value" columns={[{ title: t('Page'), dataIndex: 'value', ellipsis: true }, { title: t('Pageviews'), dataIndex: 'pageviews', align: 'right', render: num }]} />
                            </Block></Col>
                            <Col xs={24} md={12} xl={7}><Block title={t('Referrers')}>
                                <Mini rows={data.referrers || []} rowKey="value" columns={[{ title: t('Source'), dataIndex: 'value', render: (v) => v || t('Direct', { context: 'traffic' }) }, { title: t('Visitors'), dataIndex: 'sessions', align: 'right', render: num }]} />
                            </Block></Col>
                            <Col xs={24} md={12} xl={7}><Block title={t('Countries')}>
                                <Mini rows={data.countries || []} rowKey="value" columns={[{ title: t('Country'), dataIndex: 'value', render: (v) => (v ? (countryName(v) || v) : t('Unknown')) }, { title: t('Visitors'), dataIndex: 'sessions', align: 'right', render: num }]} />
                            </Block></Col>
                        </Row>
                    </div>
                )}
            </Spin>
        </div>
    );
};

export default Analytics;
